import _isEmpty from 'lodash/isEmpty';
import React, { lazy, useContext, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import PageLoader from './components/common/PageLoader';
import AppContext from './contexts/AppContext';
import { useFeatureToggle } from './services/featureToggle/FeatureToggleContext';
import { FEATURE_NAMES } from './services/featureToggle/feature-toggle.constants';
import { clearLegacyOptIn, isLegacyOptIn } from './services/featureToggle/legacyOptOut';
import {
  APP_AU_URL,
  APP_HK_URL,
  APP_MY_URL,
  APP_SG_URL,
  APP_TH_URL,
  isGlobalDomain,
  LEAD_INBOUND_URL,
  PAGE_TYPES,
} from './utils/constants';

const Overview = lazy(() => import('./pages/Overview' /* webpackChunkName: 'OverviewPage' */));
const OpportunityOverview = lazy(
  () => import('./pages/OpportunityOverview' /* webpackChunkName: 'OpportunityOverviewPage' */)
);

const PerformanceInsights = lazy(
  () => import('./pages/PerformanceInsights' /* webpackChunkName: 'PerformanceInsightsPage' */)
);

const ReportsInvoices = lazy(
  () => import('./pages/finance/reportsInvoices/ReportsInvoices' /* webpackChunkName: 'ReportsInvoicesPage' */)
);

const ReportsTransactions = lazy(
  () => import('./pages/finance/transactions/Transactions' /* webpackChunkName: 'ReportsTransactionsPage' */)
);

// TODO in same folder
const CrmPortal = lazy(() => import('./pages/CrmPortal' /* webpackChunkName: 'CrmPortalPage' */));
const CustomPush = lazy(() => import('./pages/CustomPush' /* webpackChunkName: 'CustomPushPage' */));

const OutletCodes = lazy(() => import('./pages/OutletCodes' /* webpackChunkName: 'OutletCodesPage' */));
const Settings = lazy(() => import('./pages/Settings' /* webpackChunkName: 'SettingsPage' */));

const Login = lazy(() => import('./pages/Login' /* webpackChunkName: 'LoginPage' */));
const Register = lazy(() => import('./pages/Register' /* webpackChunkName: 'RegisterPage' */));
const Home = lazy(() => import('./pages/Home' /* webpackChunkName: 'HomePage' */));

const UpdatePassword = lazy(() => import('./pages/UpdatePassword' /* webpackChunkName: 'OverviewPage' */));
const MagicLink = lazy(() => import('./pages/MagicLink' /* webpackChunkName: 'MagicLinkPage' */));
const ReportDownload = lazy(
  () => import('./pages/reports/ReportDownload' /* webpackChunkName: 'ReportDownloadPage' */)
);
const LanguageSelector = lazy(
  () => import('./pages/reports/LanguageSelector' /* webpackChunkName: 'LanguageSelectorPage' */)
);

const Admin = lazy(() => import('./pages/admin/Admin' /* webpackChunkName: 'AdminPage' */));
const CreateMerchantGroup = lazy(
  () => import('./pages/admin/CreateMerchantGroup' /* webpackChunkName: 'CreateMerchantGroupPage' */)
);
const CreateMerchantAccount = lazy(
  () => import('./pages/admin/CreateMerchantAccount' /* webpackChunkName: 'CreateMerchantAccountPage' */)
);
const CreateMerchantUser = lazy(
  () => import('./pages/admin/CreateMerchantUser' /* webpackChunkName: 'CreateMerchantUserPage' */)
);
const CreateAcl = lazy(() => import('./pages/admin/CreateAcl' /* webpackChunkName: 'CreateAclPage' */));

const Error404 = lazy(() => import('./pages/Error404' /* webpackChunkName: 'Error404Page' */));

// MOOP Self-Service pages (global domain only)
const CampaignCatalog = lazy(
  () => import('./pages/CampaignCatalog/CampaignCatalog' /* webpackChunkName: 'CampaignCatalogPage' */)
);
const CampaignDetail = lazy(() => import('./pages/CampaignDetail' /* webpackChunkName: 'CampaignDetailPage' */));
const CreateUpsize = lazy(() => import('./pages/CreateUpsize/CreateUpsize' /* webpackChunkName: 'CreateUpsizePage' */));
const CreateStandaloneOffer = lazy(
  () =>
    import('./pages/CreateStandaloneOffer/CreateStandaloneOffer' /* webpackChunkName: 'CreateStandaloneOfferPage' */)
);
const CreateAdSales = lazy(
  () => import('./pages/CreateAdSales/CreateAdSales' /* webpackChunkName: 'CreateAdSalesPage' */)
);
const AdminUserManagement = lazy(
  () => import('./pages/AdminUserManagement' /* webpackChunkName: 'AdminUserManagementPage' */)
);
const AdGroupDetail = lazy(() => import('./pages/AdGroupDetail' /* webpackChunkName: 'AdGroupDetailPage' */));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite' /* webpackChunkName: 'AcceptInvitePage' */));

// Guide-tour pages — static mock data, tour-only surfaces
const Help = lazy(() => import('./pages/Help/Help' /* webpackChunkName: 'HelpPage' */));
const GuideTourCatalog = lazy(
  () => import('./pages/GuideTour/GuideTourCatalog' /* webpackChunkName: 'GuideTourCatalogPage' */)
);
const GuideTourCampaignDetail = lazy(
  () => import('./pages/GuideTour/GuideTourCampaignDetail' /* webpackChunkName: 'GuideTourDetailPage' */)
);

// Docs
// https://reacttraining.com/react-router/web/example/route-config

class OutletAccessError extends Error {
  constructor(message) {
    super(message);
    this.name = 'noOutlets';
  }
}

// Shared routes (both domains)
const sharedRoutes = [
  {
    path: '/signin',
    component: Login,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.login',
  },
  {
    path: '/register',
    component: Register,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.login',
  },
  {
    path: '/reset-password/*',
    component: UpdatePassword,
    exact: false,
    breadcrumbs: false,
    translationKey: '',
  },
  {
    path: '/magic-link/:token',
    component: MagicLink,
    exact: true,
    breadcrumbs: false,
    translationKey: '',
  },
  {
    path: '/settings',
    component: Settings,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.settings',
  },
  {
    path: '/',
    component: Home,
    exact: true,
    breadcrumbs: false,
    translationKey: '',
    type: PAGE_TYPES.HOME,
  },
];

// Global domain routes (MOOP Self-Service)
const globalDomainRoutes = [
  {
    path: '/opportunity-overview',
    component: OpportunityOverview,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.opportunityOverview',
    phaseILanding: true,
  },
  {
    path: '/campaigns',
    component: CampaignCatalog,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.campaigns',
    phaseIIGated: true,
  },
  {
    path: '/campaigns/:id',
    component: CampaignDetail,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.campaigns',
    phaseIIGated: true,
  },
  // Guide-tour routes — static mock surfaces, separate from real pages
  {
    path: '/help',
    component: Help,
    exact: true,
    breadcrumbs: false,
    translationKey: '',
  },
  {
    path: '/help/tour/campaigns/:id',
    component: GuideTourCampaignDetail,
    exact: true,
    breadcrumbs: false,
    translationKey: '',
    phaseIIGated: true,
  },
  {
    path: '/help/tour/campaigns',
    component: GuideTourCatalog,
    exact: true,
    breadcrumbs: false,
    translationKey: '',
    phaseIIGated: true,
  },
  {
    path: '/create/upsize',
    component: CreateUpsize,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.opportunityOverview',
    phaseIIGated: true,
  },
  {
    path: '/create/standalone-offer',
    component: CreateStandaloneOffer,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.opportunityOverview',
    phaseIIGated: true,
  },
  {
    path: '/create/ad-sales',
    component: CreateAdSales,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.opportunityOverview',
    phaseIIGated: true,
  },
  {
    path: '/admin/ad-group/:id',
    component: AdGroupDetail,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.opportunityOverview',
    phaseIIGated: true,
  },
  {
    path: '/admin',
    component: AdminUserManagement,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.overview',
    phaseIIGated: true,
  },
  {
    path: '/accept-invite',
    component: AcceptInvite,
    exact: true,
    breadcrumbs: false,
    translationKey: '',
    phaseIIGated: true,
  },
];

// Origin domain routes (existing merchant portal)
const originDomainRoutes = [
  {
    path: '/opportunity-overview',
    component: OpportunityOverview,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.opportunityOverview',
  },
  {
    path: '/customer-insights',
    component: () => <Redirect to="/performance-insights" />,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.segmentation',
  },
  {
    path: '/performance-insights',
    component: PerformanceInsights,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.performanceInsights',
  },
  {
    path: '/finance/invoice',
    component: ReportsInvoices,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.finance.dropdown.invoices',
  },
  {
    path: '/transactions',
    component: ReportsTransactions,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.finance.dropdown.transactions',
  },
  {
    path: '/broadcast/create-a-notification',
    component: CustomPush,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.crm.customPush',
  },
  {
    path: '/broadcast',
    component: CrmPortal,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.crm.index',
  },
  {
    path: '/outlet-codes',
    component: OutletCodes,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.outletCodes',
  },
  {
    path: '/overview',
    component: Overview,
    exact: true,
    breadcrumbs: true,
    translationKey: 'menu.overview',
    type: PAGE_TYPES.AUTH_HOME,
  },
  {
    path: '/reports/:reportType/:downloadId',
    component: ReportDownload,
    exact: true,
    breadcrumbs: false,
    translationKey: '',
  },
  {
    path: '/select-language/:reportType/:downloadId',
    component: LanguageSelector,
    exact: true,
    breadcrumbs: false,
    translationKey: '',
  },

  // Admin Routes (origin)
  {
    path: '/admin/create-merchant-group',
    component: CreateMerchantGroup,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.overview',
  },
  {
    path: '/admin/create-merchant-account',
    component: CreateMerchantAccount,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.overview',
  },
  {
    path: '/admin/create-acl',
    component: CreateAcl,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.overview',
  },
  {
    path: '/admin/create-merchant-user',
    component: CreateMerchantUser,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.overview',
  },
  {
    path: '/admin',
    component: Admin,
    exact: true,
    breadcrumbs: false,
    translationKey: 'menu.overview',
  },
];

/** @returns {any[]} */
const getRoutesConfig = () =>
  isGlobalDomain() ? [...globalDomainRoutes, ...sharedRoutes] : [...originDomainRoutes, ...sharedRoutes];

// Redirects non-canary users away from Phase II routes.
// isLoading defaults to hidden (false) so the nav never flashes Phase II content.
const PhaseIIGuard = ({ children }) => {
  const { isEnabled, isLoading } = useFeatureToggle(FEATURE_NAMES.MOOP_PHASE_II);
  // Reaching a Phase II route is how the merchant comes back from the legacy surface, so the opt-out
  // is dropped here. In an effect, not during render — clearing storage while rendering would make
  // this component impure.
  useEffect(() => {
    if (isEnabled) clearLegacyOptIn();
  }, [isEnabled]);
  if (isLoading) return <PageLoader />;
  if (!isEnabled) return <Redirect to="/opportunity-overview" />;
  return <>{children}</>;
};

// Mirror of PhaseIIGuard for the Phase I landing page. Canary users have no
// Opportunity overview entry in the nav, so /campaigns is their default surface —
// this catches every way they can still land on /opportunity-overview (post-login
// redirect, magic link, MoopAuthGuard bounce, bookmarks) in one place. Resolving
// here rather than at each redirect site avoids racing the async toggle fetch,
// which only starts once the user is known.
// A merchant who chose "Back to Legacy Merchant Portal" is exempt: without that check the nav entry would bounce
// straight back here and the escape hatch would be unusable.
const PhaseILandingRedirect = ({ children }) => {
  const { isEnabled, isLoading } = useFeatureToggle(FEATURE_NAMES.MOOP_PHASE_II);
  if (isLoading) return <PageLoader />;
  if (isEnabled && !isLegacyOptIn()) return <Redirect to="/campaigns" />;
  return <>{children}</>;
};

const RouteWithSubRoutes = (route) => (
  <Route
    path={route.path}
    exact={route.exact}
    render={(props) => {
      if (route.component) {
        if (route.path === '/register' || route.path === '/') {
          if (window.location.host === APP_SG_URL) {
            window.location.href = `${LEAD_INBOUND_URL}/sg`;
            return null;
          }
          if (window.location.host === APP_AU_URL) {
            window.location.href = `${LEAD_INBOUND_URL}/au`;
            return null;
          }
          if (window.location.host === APP_TH_URL) {
            window.location.href = `${LEAD_INBOUND_URL}/th/paylater`;
            return null;
          }
          if (window.location.host === APP_MY_URL) {
            window.location.href = `${LEAD_INBOUND_URL}/my`;
            return null;
          }
          if (window.location.host === APP_HK_URL) {
            window.location.href = `${LEAD_INBOUND_URL}/hk`;
            return null;
          }
        }
        // pass the sub-routes down to keep nesting
        const content = <route.component {...props} routes={route.routes} />;
        if (route.phaseIIGated) return <PhaseIIGuard>{content}</PhaseIIGuard>;
        if (route.phaseILanding) return <PhaseILandingRedirect>{content}</PhaseILandingRedirect>;
        return content;
      }
      return null;
    }}
  />
);

const Routes = () => {
  const appContext = useContext(AppContext);
  const { brandsAndOutlets, iusAndOutlets } = appContext;

  useEffect(() => {
    /**
     * to trigger this check on every routes.
     * Skip brand access check for business-mt-staging.shopback.com domain
     */
    if (isGlobalDomain()) {
      return; // Skip outlet access check for staging domain
    }

    if (brandsAndOutlets !== null && _isEmpty(brandsAndOutlets) && _isEmpty(iusAndOutlets)) {
      throw new OutletAccessError("You don't have access to any outlets");
    }
  }, [brandsAndOutlets]);

  const routesConfig = getRoutesConfig();

  return (
    <Switch>
      {routesConfig.map((route) => (
        <RouteWithSubRoutes key={route.path} {...route} />
      ))}
      <Route component={Error404} />
    </Switch>
  );
};

export { getRoutesConfig, PhaseIIGuard, PhaseILandingRedirect, Routes as default, RouteWithSubRoutes };
